/* KYO cart drawer: renders /cart.js, opens after add-to-cart (kyo:cart-updated) and from the header cart link.
   Lines with per-day price and real savings, subscription upgrade, upsell, totals, empty-state product card. */
(() => {
  const root = document.querySelector('[data-kyo-cart]');
  const configEl = document.querySelector('[data-kyo-cart-config]');
  if (!root || !configEl) return;
  const config = JSON.parse(configEl.textContent);
  const $ = sel => root.querySelector(sel);
  const money = cents => new Intl.NumberFormat(config.locale === 'en' ? 'en-NL' : 'nl-NL', { style: 'currency', currency: config.currency || 'EUR' }).format(cents / 100).replace(/\s/g, '');
  const esc = s => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const track = (name, data) => window.kyoTrack?.(name, data);
  let lastFocus = null;
  let addedTimer = 0;
  document.documentElement.classList.add('kyo-has-cart-drawer');

  function open(justAdded = false) {
    if (root.hidden) lastFocus = document.activeElement;
    root.hidden = false;
    requestAnimationFrame(() => root.classList.add('is-open'));
    document.documentElement.classList.add('kyo-cart-open');
    $('.kyo-cart__panel').focus();
    const added = $('[data-kyo-cart-added]');
    added.hidden = !justAdded;
    clearTimeout(addedTimer);
    if (justAdded) addedTimer = setTimeout(() => { added.hidden = true; }, 3500);
  }
  function close() {
    root.classList.remove('is-open');
    document.documentElement.classList.remove('kyo-cart-open');
    setTimeout(() => { root.hidden = true; }, 300);
    lastFocus?.focus?.();
  }
  async function fetchCart() {
    const response = await fetch(`${config.root}cart.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  }
  async function post(path, body) {
    root.classList.add('is-busy');
    try {
      const response = await fetch(`${config.root}${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const result = await response.json();
      const cart = path === 'cart/add.js' ? await fetchCart() : result;
      render(cart);
      document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
      return cart;
    } finally { root.classList.remove('is-busy'); }
  }
  const change = (key, quantity, extra = {}) => post('cart/change.js', { id: key, quantity, ...extra });

  function deliveryText() {
    if (!config.deliveryBefore) return '';
    return new Date().getHours() < Number(config.deliveryHour) ? config.deliveryBefore : config.deliveryAfter;
  }

  function lineHtml(item) {
    const units = item.quantity;
    const perDay = config.daysPerUnit > 0 ? item.final_line_price / (units * config.daysPerUnit) : 0;
    // Subscription lines keep their regular price in selling_plan_allocation.compare_at_price (per unit).
    const planCompare = item.selling_plan_allocation?.compare_at_price ? item.selling_plan_allocation.compare_at_price * units : 0;
    const before = Math.max(item.original_line_price, planCompare);
    const saving = before - item.final_line_price;
    return `
      <div class="kyo-cart__line" data-key="${esc(item.key)}">
        ${item.image ? `<img src="${esc(item.image)}${item.image.includes('?') ? '&' : '?'}width=180" width="80" height="80" alt="" loading="lazy">` : '<span class="kyo-cart__noimg"></span>'}
        <div class="kyo-cart__info">
          <a href="${esc(item.url)}">${esc(item.product_title)}</a>
          <small>${item.selling_plan_allocation ? esc(item.selling_plan_allocation.selling_plan.name) : esc(config.lineMeta)}</small>
          ${perDay > 0 ? `<small class="kyo-cart__perday">${money(Math.round(perDay))} ${esc(config.perDay)}</small>` : ''}
          <div class="kyo-cart__qty">
            <button type="button" data-qty="${units - 1}" aria-label="Eén minder">−</button><span aria-live="polite">${units}</span><button type="button" data-qty="${units + 1}" aria-label="Eén meer">+</button>
            <button type="button" class="kyo-cart__remove" data-qty="0">${esc(config.remove)}</button>
          </div>
        </div>
        <div class="kyo-cart__price">
          ${saving > 0 ? `<s>${money(before)}</s>` : ''}
          <strong>${money(item.final_line_price)}</strong>
          ${saving > 0 ? `<em>${esc(config.savingLabel)} ${money(saving)}</em>` : ''}
        </div>
      </div>`;
  }

  function render(cart) {
    const hasItems = cart.item_count > 0;
    $('[data-kyo-cart-count]').textContent = hasItems ? `(${cart.item_count})` : '';
    $('[data-kyo-cart-lines]').innerHTML = cart.items.map(lineHtml).join('');
    $('[data-kyo-cart-empty]').hidden = hasItems;
    $('[data-kyo-cart-foot]').hidden = !hasItems;
    const trust = $('[data-kyo-cart-trust]'); if (trust) trust.hidden = !hasItems;
    const review = $('[data-kyo-cart-review]'); if (review) review.hidden = !hasItems;

    // totals: original subtotal, real savings (line + cart-level discounts), shipping
    const original = cart.items.reduce((sum, item) => sum + Math.max(item.original_line_price, (item.selling_plan_allocation?.compare_at_price || 0) * item.quantity), 0);
    const saving = Math.max(0, original - cart.total_price);
    $('[data-kyo-cart-original]').textContent = money(original);
    $('[data-kyo-cart-saving-row]').hidden = saving <= 0;
    $('[data-kyo-cart-saving]').textContent = `−${money(saving)}`;
    const freeShipping = config.threshold > 0 && cart.total_price >= config.threshold;
    $('[data-kyo-cart-shipping-cost]').textContent = freeShipping ? config.shippingFree : config.shippingCalc;
    $('[data-kyo-cart-shipping-cost]').classList.toggle('is-free', freeShipping);
    $('[data-kyo-cart-total]').textContent = money(cart.total_price);

    // free shipping progress + delivery promise
    const shipping = $('[data-kyo-cart-shipping]');
    if (config.threshold > 0 && hasItems) {
      const left = Math.max(0, config.threshold - cart.total_price);
      shipping.hidden = false;
      $('[data-kyo-cart-shipping-text]').textContent = left > 0 ? config.shippingTodo.replace('[bedrag]', money(left)) : config.shippingDone;
      $('[data-kyo-cart-bar]').style.transform = `scaleX(${Math.min(1, cart.total_price / config.threshold)})`;
      shipping.classList.toggle('is-done', left === 0);
    } else shipping.hidden = !hasItems;
    const delivery = $('[data-kyo-cart-delivery]'); if (delivery) delivery.textContent = deliveryText();

    // subscription upgrade: only one-time lines, only with a real selling plan
    const oneTime = cart.items.filter(item => !item.selling_plan_allocation);
    const subCard = $('[data-kyo-cart-sub]');
    const subTarget = config.subUpgrade && config.sellingPlan ? oneTime[0] : null;
    subCard.hidden = !subTarget;
    if (subTarget) {
      $('[data-kyo-cart-sub-btn]').dataset.key = subTarget.key;
      const pct = config.unitPrice > 0 && config.subPrice > 0 ? Math.round((1 - config.subPrice / config.unitPrice) * 100) : 0;
      const savingEl = $('[data-kyo-cart-sub-saving]');
      savingEl.textContent = pct > 0 ? `· ${(savingEl.dataset.template || 'save {pct}%').replace('{pct}', pct)}` : '';
    }

    // upsell: one-time line below the recommended quantity, text depends on how many are in the cart
    const upsell = $('[data-kyo-cart-upsell]');
    const candidate = oneTime.find(item => item.quantity < config.upsellQty);
    upsell.hidden = !(candidate && config.upsellQty > 1);
    if (candidate) {
      const two = candidate.quantity === config.upsellQty - 1 && config.upsellTitle2;
      $('[data-kyo-cart-upsell-title]').textContent = two ? config.upsellTitle2 : config.upsellTitle;
      $('[data-kyo-cart-upsell-text]').textContent = two ? config.upsellText2 : config.upsellText;
      $('[data-kyo-cart-upsell-btn]').dataset.key = candidate.key;
    }
  }

  root.addEventListener('click', event => {
    if (event.target.closest('[data-kyo-cart-close]')) return close();
    const qty = event.target.closest('[data-qty]');
    if (qty) return change(qty.closest('[data-key]').dataset.key, Math.max(0, Number(qty.dataset.qty)));
    const up = event.target.closest('[data-kyo-cart-upsell-btn]');
    if (up) { track('kyo_cart_upsell', {}); return change(up.dataset.key, config.upsellQty).then(() => $('.kyo-cart__panel').focus()); }
    const sub = event.target.closest('[data-kyo-cart-sub-btn]');
    if (sub) {
      const item = [...root.querySelectorAll('.kyo-cart__line')].find(line => line.dataset.key === sub.dataset.key);
      const quantity = Number(item?.querySelector('.kyo-cart__qty span')?.textContent || 1);
      track('kyo_cart_subscription_upgrade', { quantity });
      return change(sub.dataset.key, quantity, { selling_plan: config.sellingPlan }).then(() => $('.kyo-cart__panel').focus());
    }
    const add = event.target.closest('[data-kyo-cart-add]');
    if (add) return post('cart/add.js', { items: [{ id: Number(add.dataset.kyoCartAdd), quantity: 1 }] });
    if (event.target.closest('[data-kyo-cart-checkout]')) track('kyo_checkout_click', { source: 'cart_drawer' });
  });
  document.addEventListener('keydown', event => {
    if (root.hidden) return;
    if (event.key === 'Escape') close();
    if (event.key === 'Tab') { // keep focus inside the drawer
      const items = [...root.querySelectorAll('button, a[href], [tabindex="0"]')].filter(el => el.offsetParent);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });
  document.addEventListener('kyo:cart-updated', event => { render(event.detail); open(true); });
  document.addEventListener('click', async event => {
    const link = event.target.closest('a[href$="/cart"], a.kyo-cart-link');
    if (!link || event.metaKey || event.ctrlKey || root.contains(link)) return;
    event.preventDefault();
    render(await fetchCart());
    open(false);
  });
})();
